/**
 * Command-activity contract helpers for the site command stream.
 *
 * Contains:
 * - aggregate normalization and visible optimistic-state resolution
 * - local request lifecycle transforms
 * - generated-at socket ordering and command interlock helpers
 *
 * Returns plain JS — no JSX, redux, or network I/O.
 */

const COMMAND_ACTIVITY_STATES = ['idle', 'queued', 'sending'];

export const COMMAND_ACTIVITY_SOURCE = Object.freeze({
  REST: 'rest',
  SOCKET: 'socket',
});

export const IDLE_COMMAND_ACTIVITY = Object.freeze({
  state: 'idle',
  pending_count: 0,
  pending_since: null,
});

export const LOCAL_SENDING_COMMAND_ACTIVITY = Object.freeze({
  state: 'sending',
  pending_count: 1,
  pending_since: null,
});

export function normalizeCommandActivity(activity) {
  const state = COMMAND_ACTIVITY_STATES.includes(activity?.state) ? activity.state : 'idle';
  const pendingCount =
    Number.isInteger(activity?.pending_count) && activity.pending_count >= 0 ? activity.pending_count : 0;

  return {
    state,
    pending_count: pendingCount,
    pending_since: typeof activity?.pending_since === 'string' ? activity.pending_since : null,
  };
}

export function createCommandActivityEntry(activity, generatedAt = null) {
  return {
    activity: normalizeCommandActivity(activity),
    generatedAt: generatedAt ?? null,
  };
}

/** Show an immediate sending state while an HTTP write owns the local lease. */
export function resolveVisibleCommandActivity(activity, localRequestId = null) {
  const authoritative = activity ?? IDLE_COMMAND_ACTIVITY;
  if (localRequestId != null && normalizeCommandActivity(authoritative).state === 'idle') {
    return LOCAL_SENDING_COMMAND_ACTIVITY;
  }
  return authoritative;
}

function normalizeCommandActivityEntry(entry) {
  return {
    ...createCommandActivityEntry(entry?.activity, entry?.generatedAt),
    localRequestId: entry?.localRequestId ?? null,
  };
}

export function beginCommandActivityRequest(entry, requestId) {
  return {
    ...normalizeCommandActivityEntry(entry),
    localRequestId: requestId,
  };
}

export function endCommandActivityRequest(entry, requestId) {
  const current = normalizeCommandActivityEntry(entry);
  if (current.localRequestId !== requestId) return current;
  return { ...current, localRequestId: null };
}

export function promoteCommandActivityRequest(entry, requestId, pendingCount = 1) {
  const current = normalizeCommandActivityEntry(entry);
  if (current.localRequestId !== requestId) return current;

  const count = Number.isInteger(pendingCount) && pendingCount > 0 ? pendingCount : 1;
  const activity = isCommandActivityLocked(current.activity)
    ? current.activity
    : {
        state: 'queued',
        pending_count: count,
        pending_since: null,
      };

  return {
    ...current,
    activity,
    localRequestId: null,
  };
}

export function replaceCommandActivityEntry(entry, activity, generatedAt = null) {
  const current = normalizeCommandActivityEntry(entry);
  const replacement = createCommandActivityEntry(activity, generatedAt ?? current.generatedAt);
  const preserveLocalRequest = replacement.activity.state === 'idle' && current.localRequestId != null;

  return {
    ...replacement,
    localRequestId: preserveLocalRequest ? current.localRequestId : null,
  };
}

export function resolveCommandActivitySocketUpdate(payload, meta, { lastGeneratedAt = null } = {}) {
  if (!payload || meta?.siteId == null) return null;

  const generatedAt = meta.generatedAt ?? null;
  if (generatedAt != null && lastGeneratedAt != null && new Date(generatedAt) < new Date(lastGeneratedAt)) {
    return null;
  }

  return {
    siteId: String(meta.siteId),
    ...createCommandActivityEntry(payload, generatedAt),
  };
}

export function getCommandActivityIndicatorState(activity) {
  const normalized = normalizeCommandActivity(activity);
  if (normalized.state === 'idle') return null;

  return {
    state: normalized.state,
    pendingCount: normalized.pending_count,
  };
}

export function isCommandActivityLocked(activity) {
  return normalizeCommandActivity(activity).state !== 'idle';
}

export function isSiteCommandLocked(activity, localDispatching = false, activeUpgrade = false) {
  return Boolean(activeUpgrade) || Boolean(localDispatching) || isCommandActivityLocked(activity);
}

// Polling while locked is load-bearing: only a REST snapshot (with a newer
// request version) retires the accepted-dispatch lease, so socket events
// alone cannot release the command lock after a local write.
export function shouldPollCommandActivity(activity) {
  return isCommandActivityLocked(activity);
}
